import { useRef, useState, useEffect } from 'react'
import { proxy, useSnapshot } from 'valtio'
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom'

const state = proxy({
  lightbox: null,
  items: [
    { url: '/portfolio/1.mp4', type: 'video' },
    { url: '/portfolio/2.jpg', type: 'image' },
    { url: '/portfolio/3.jpg', type: 'image' },
    { url: '/portfolio/4.jpg', type: 'image' },
    { url: '/portfolio/5.jpg', type: 'image' },
    { url: '/portfolio/6.jpg', type: 'image' },
    { url: '/portfolio/7.jpg', type: 'image' },
    { url: '/portfolio/8.jpg', type: 'image' },
    { url: '/portfolio/9.jpg', type: 'image' },
    { url: '/portfolio/9.webp', type: 'image' },
    { url: '/portfolio/10.webp', type: 'image' },
    { url: '/portfolio/11.jpg', type: 'image' },
    { url: '/portfolio/12.jpg', type: 'image' },
    { url: '/portfolio/13.jpg', type: 'image' },
    { url: '/portfolio/14.jpg', type: 'image' },
    { url: '/portfolio/15.mp4', type: 'video' },
    { url: '/portfolio/16.jpg', type: 'image' },
    { url: '/portfolio/17.webp', type: 'image' },
    { url: '/portfolio/18.webp', type: 'image' },
    { url: '/portfolio/19.jpg', type: 'image' },
    { url: '/portfolio/20.jpg', type: 'image' },
    { url: '/portfolio/21.jpg', type: 'image' },
    { url: '/portfolio/22.jpg', type: 'image' },
  ],
})

// ============ HOME PAGE ============
function HomePage() {
  return (
    <div className="page">
      <div className="content">
        <h1 className="brand">shea.glass</h1>
        <p className="tagline">coming soon</p>
        <div className="divider" />
        <a
          href="mailto:shea@shea.glass,Sheaglass91@gmail.com"
          className="contact-btn"
        >
          Contact Me
        </a>
      </div>
      <footer className="footer">
        <span>&copy; {new Date().getFullYear()} shea.glass</span>
      </footer>
    </div>
  )
}

// ============ PORTFOLIO PAGE ============
function GridItem({ item, index }) {
  const [hovered, setHovered] = useState(false)
  const videoRef = useRef(null)

  const handleClick = () => {
    state.lightbox = { url: item.url, type: item.type }
  }

  const handleMouseEnter = () => {
    setHovered(true)
    if (videoRef.current) {
      videoRef.current.play().catch(() => {})
    }
  }

  const handleMouseLeave = () => {
    setHovered(false)
    if (videoRef.current) {
      videoRef.current.pause()
    }
  }

  return (
    <div
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        aspectRatio: '16 / 9',
        overflow: 'hidden',
        cursor: 'pointer',
        borderRadius: '4px',
        transform: hovered ? 'scale(1.03)' : 'scale(1)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        boxShadow: hovered ? '0 8px 30px rgba(0,0,0,0.5)' : '0 2px 10px rgba(0,0,0,0.3)',
        zIndex: hovered ? 10 : 1,
      }}
    >
      {item.type === 'video' ? (
        <video
          ref={videoRef}
          src={encodeURI(item.url)}
          muted
          loop
          playsInline
          preload="metadata"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: hovered ? 'grayscale(0)' : 'grayscale(0.5)',
            transition: 'filter 0.3s ease',
          }}
        />
      ) : (
        <img
          src={encodeURI(item.url)}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: hovered ? 'grayscale(0)' : 'grayscale(0.5)',
            transition: 'filter 0.3s ease',
          }}
        />
      )}
    </div>
  )
}

function Grid() {
  const { items } = useSnapshot(state)

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '16px',
        padding: '100px 40px 40px 40px',
        maxWidth: '1800px',
        margin: '0 auto',
      }}
    >
      {items.map((item, i) => (
        <GridItem key={i} item={item} index={i} />
      ))}
    </div>
  )
}

function Lightbox() {
  const { lightbox, items } = useSnapshot(state)
  const [visible, setVisible] = useState(false)
  const [content, setContent] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const scrollTimeout = useRef(null)
  const isScrolling = useRef(false)

  useEffect(() => {
    if (lightbox) {
      const index = items.findIndex(item => item.url === lightbox.url)
      setCurrentIndex(index >= 0 ? index : 0)
      setContent(lightbox)
      setDirection(0)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true)
        })
      })
    } else {
      setVisible(false)
      const timer = setTimeout(() => setContent(null), 500)
      return () => clearTimeout(timer)
    }
  }, [lightbox, items])

  const goTo = (newIndex, dir) => {
    if (newIndex < 0 || newIndex >= items.length) return
    if (isScrolling.current) return
    isScrolling.current = true
    setDirection(dir)
    setVisible(false)
    setTimeout(() => {
      const item = items[newIndex]
      setContent({ url: item.url, type: item.type })
      setCurrentIndex(newIndex)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true)
          setTimeout(() => {
            isScrolling.current = false
          }, 300)
        })
      })
    }, 200)
  }

  const prev = (e) => {
    if (e) e.stopPropagation()
    goTo(currentIndex - 1, -1)
  }

  const next = (e) => {
    if (e) e.stopPropagation()
    goTo(currentIndex + 1, 1)
  }

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!content) return
      if (e.key === 'ArrowLeft') goTo(currentIndex - 1, -1)
      if (e.key === 'ArrowRight') goTo(currentIndex + 1, 1)
      if (e.key === 'Escape') state.lightbox = null
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [content, currentIndex])

  useEffect(() => {
    const handleWheel = (e) => {
      if (!content) return
      e.preventDefault()

      if (scrollTimeout.current) return

      scrollTimeout.current = setTimeout(() => {
        scrollTimeout.current = null
      }, 400)

      if (e.deltaY > 0 || e.deltaX > 0) {
        goTo(currentIndex + 1, 1)
      } else if (e.deltaY < 0 || e.deltaX < 0) {
        goTo(currentIndex - 1, -1)
      }
    }

    if (content) {
      window.addEventListener('wheel', handleWheel, { passive: false })
    }
    return () => window.removeEventListener('wheel', handleWheel)
  }, [content, currentIndex])

  const close = () => {
    state.lightbox = null
  }

  if (!content) return null

  const canGoPrev = currentIndex > 0
  const canGoNext = currentIndex < items.length - 1

  return (
    <div
      onClick={close}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: visible ? 'rgba(0,0,0,0.96)' : 'rgba(0,0,0,0)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        cursor: 'pointer',
        transition: 'background 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          close()
        }}
        style={{
          position: 'absolute',
          top: '16px',
          right: '20px',
          color: 'white',
          fontSize: '40px',
          fontWeight: '300',
          cursor: 'pointer',
          opacity: visible ? 0.8 : 0,
          transition: 'opacity 0.4s ease',
          transitionDelay: '0.2s',
          zIndex: 1010,
          padding: '12px 16px',
          background: 'rgba(0,0,0,0.5)',
          borderRadius: '4px',
          border: 'none',
          lineHeight: 1,
        }}
        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
        onMouseLeave={(e) => e.currentTarget.style.opacity = 0.8}
      >
        ×
      </button>

      {/* Prev button */}
      {canGoPrev && (
        <div
          onClick={prev}
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            fontSize: '56px',
            fontWeight: '200',
            cursor: 'pointer',
            opacity: visible ? 0.5 : 0,
            transition: 'opacity 0.4s ease',
            userSelect: 'none',
            padding: '20px',
            zIndex: 10,
          }}
          onMouseEnter={(e) => e.target.style.opacity = 1}
          onMouseLeave={(e) => e.target.style.opacity = 0.5}
        >
          ‹
        </div>
      )}

      {/* Next button */}
      {canGoNext && (
        <div
          onClick={next}
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            fontSize: '56px',
            fontWeight: '200',
            cursor: 'pointer',
            opacity: visible ? 0.5 : 0,
            transition: 'opacity 0.4s ease',
            userSelect: 'none',
            padding: '20px',
            zIndex: 10,
          }}
          onMouseEnter={(e) => e.target.style.opacity = 1}
          onMouseLeave={(e) => e.target.style.opacity = 0.5}
        >
          ›
        </div>
      )}

      {/* Counter */}
      <div
        style={{
          position: 'absolute',
          bottom: '16px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'white',
          fontSize: '13px',
          fontWeight: '300',
          letterSpacing: '0.15em',
          opacity: visible ? 0.4 : 0,
          transition: 'opacity 0.4s ease',
        }}
      >
        {currentIndex + 1} / {items.length}
      </div>

      {/* Content */}
      {content.type === 'video' ? (
        <video
          key={content.url}
          src={encodeURI(content.url)}
          autoPlay
          loop
          muted
          controls
          playsInline
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: '90vw',
            maxHeight: '85vh',
            objectFit: 'contain',
            background: '#000',
            transform: visible ? 'scale(1) translateX(0)' : `scale(0.95) translateX(${direction * 80}px)`,
            opacity: visible ? 1 : 0,
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: '4px',
          }}
        />
      ) : (
        <img
          key={content.url}
          src={encodeURI(content.url)}
          alt=""
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: '90vw',
            maxHeight: '85vh',
            objectFit: 'contain',
            transform: visible ? 'scale(1) translateX(0)' : `scale(0.95) translateX(${direction * 80}px)`,
            opacity: visible ? 1 : 0,
            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: '4px',
          }}
        />
      )}
    </div>
  )
}

function PortfolioPage() {
  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a' }}>
      <Link
        to="/"
        style={{
          position: 'fixed',
          top: '24px',
          left: '32px',
          zIndex: 100,
          color: 'white',
          textDecoration: 'none',
          fontSize: '1.2rem',
          fontWeight: 500,
          letterSpacing: '-0.02em',
        }}
      >
        shea.glass
      </Link>
      <Grid />
      <Lightbox />
    </div>
  )
}

// ============ APP WITH ROUTES ============
export const App = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
    </Routes>
  </BrowserRouter>
)
